/**
 * Crew jobs on the Grid (11/3, "go core"): two or more techs on ONE job while
 * the rest of their day stays their own (`job_assignments`, Issue #1245).
 * Not a day pairing — nobody becomes a helper and nobody loses their row.
 *
 * One place for the card-face words so the classic and new boards agree:
 * "👥 2 · William + Mike", and the "Remove Mike from this job" menu items.
 */
import type { DispatchJob } from "./dispatch-types";

type CrewJob = Pick<DispatchJob, "assigned_to" | "assigned_to_name" | "crew">;

export interface CrewMember {
    userId: string;
    /** Full name as the server sends it ("Mike Kent"). */
    name: string;
    firstName: string;
    role: string;
    earningMode?: string;
}

function firstName(name: string | null | undefined): string {
    return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/** The added techs on a job — never the lead (the lead can't be removed here). */
export function crewMembers(job: CrewJob): CrewMember[] {
    return (job.crew ?? [])
        .filter((c) => c.user_id && c.user_id !== job.assigned_to)
        .map((c) => ({
            userId: c.user_id,
            name: (c.name ?? "").trim() || "Tech",
            firstName: firstName(c.name) || "Tech",
            role: c.role,
            earningMode: c.earning_mode,
        }));
}

export interface CrewChip {
    /** Everyone on the job, lead included. */
    count: number;
    /** "William + Mike" — lead first. */
    names: string;
    /** Hover words. */
    title: string;
}

/** Null when the job has only its lead. */
export function crewChip(job: CrewJob): CrewChip | null {
    const members = crewMembers(job);
    if (members.length === 0) return null;
    const lead = firstName(job.assigned_to_name) || "Lead";
    const names = [lead, ...members.map((m) => m.firstName)].join(" + ");
    const fullNames = [(job.assigned_to_name ?? "").trim() || "Lead", ...members.map((m) => m.name)];
    return {
        count: 1 + members.length,
        names,
        title: `${fullNames.length} techs on this job: ${fullNames.join(", ")}. ${lead} leads it. It shows on each of their rows; the rest of their day is their own.`,
    };
}

/** "Remove Mike from this job" */
export function removeCrewLabel(member: Pick<CrewMember, "firstName">): string {
    return `Remove ${member.firstName} from this job`;
}
